/* Try the look: stage of looks + timeline + "In this look" panel with per-piece and full-look add. */
const SIM = window.SIM || {};
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

// Distance from the centre → horizontal offset in "steps" (neighbours tuck in as they shrink).
const OFFSET = [0, 1, 1.78, 2.42];

function formatMoney(cents) {
  const format = (SIM.config && SIM.config.moneyFormat) || '${{amount}}';
  const group = (n, sep) => n.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  const fixed = (d) => (cents / 100).toFixed(d);
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return group(fixed(0), ',');
      case 'amount_with_comma_separator': return group(fixed(2).replace('.', ','), '.');
      case 'amount_no_decimals_with_comma_separator': return group(fixed(0), '.');
      case 'amount_with_apostrophe_separator': return group(fixed(2), "'");
      default: { const [a, b] = fixed(2).split('.'); return `${group(a, ',')}.${b}`; }
    }
  });
}

if (!customElements.get('try-the-look')) {
  customElements.define('try-the-look', class extends HTMLElement {
    connectedCallback() {
      this.looks = $$('[data-look]', this);
      this.ticks = $$('[data-go]', this);
      this.panels = $$('[data-look-panel]', this);
      this.stage = $('[data-stage]', this);
      this.toggle = $('[data-toggle]', this);
      this.nameEl = $('[data-current-name]', this);
      if (!this.looks.length) return;

      this.index = 0;
      this.reduced = !(SIM.motionOK ? SIM.motionOK() : !matchMedia('(prefers-reduced-motion: reduce)').matches);
      this.autoplay = this.hasAttribute('data-autoplay') && !this.reduced && this.looks.length > 1;
      if (!this.autoplay && this.toggle) this.toggle.hidden = true;
      this.userPaused = false;

      this.looks.forEach((el) => el.addEventListener('click', () => this.go(Number(el.dataset.look), true)));
      this.ticks.forEach((el) => el.addEventListener('click', () => this.go(Number(el.dataset.go), true)));
      $('[data-prev]', this)?.addEventListener('click', () => this.go(this.index - 1, true));
      $('[data-next]', this)?.addEventListener('click', () => this.go(this.index + 1, true));
      this.toggle?.addEventListener('click', () => this.setPaused(!this.userPaused, true));
      this.stage.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const forward = (e.key === 'ArrowRight') !== (document.dir === 'rtl');
        e.preventDefault();
        this.go(this.index + (forward ? 1 : -1), true);
        this.looks[this.index].focus();
      });

      this.bindSwipe();
      this.bindCursor();
      this.bindPanel();

      // Autoplay is driven by the active tick's CSS progress animation.
      this.addEventListener('animationend', (e) => {
        if (e.target.matches('[data-progress]') && this.autoplay) this.go(this.index + 1);
      });
      // Hold while the shopper is reading the panel or the section is off screen.
      const panel = $('.ttl__panel', this);
      ['pointerenter', 'focusin'].forEach((t) => panel?.addEventListener(t, () => this.hold(true)));
      panel?.addEventListener('pointerleave', () => this.hold(false));
      panel?.addEventListener('focusout', (e) => { if (!panel.contains(e.relatedTarget)) this.hold(false); });
      this.observer = new IntersectionObserver(([entry]) => this.toggleAttribute('data-offscreen', !entry.isIntersecting), { threshold: 0.25 });
      this.observer.observe(this);

      this.toggleAttribute('data-playing', this.autoplay);
      this.render();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    go(i, fromUser = false) {
      const n = this.looks.length;
      this.index = ((i % n) + n) % n;
      if (fromUser && this.autoplay && !this.userPaused) this.restartProgress();
      this.render();
    }

    render() {
      const n = this.looks.length;
      this.looks.forEach((el, i) => {
        let d = i - this.index;
        if (d > n / 2) d -= n;
        if (d < -n / 2) d += n;
        const ad = Math.min(Math.abs(d), 3);
        el.style.setProperty('--x', (Math.sign(d) * OFFSET[ad]).toFixed(2));
        el.style.setProperty('--ad', ad);
        el.style.zIndex = 10 - ad;
        el.toggleAttribute('data-far', Math.abs(d) > 2);
        el.toggleAttribute('data-active', d === 0);
        if (d === 0) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
        el.tabIndex = d === 0 ? 0 : -1;
      });
      this.ticks.forEach((el, i) => {
        if (i === this.index) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
      this.panels.forEach((el, i) => { el.hidden = i !== this.index; });
      if (this.nameEl) this.nameEl.textContent = $('.ttl__tick-name', this.ticks[this.index])?.textContent || '';
    }

    restartProgress() {
      const fill = $('[data-progress]', this.ticks[this.index]);
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = '';
    }

    setPaused(paused, fromUser = false) {
      if (!this.autoplay) return;
      if (fromUser) this.userPaused = paused;
      this.toggleAttribute('data-playing', !this.userPaused);
      if (this.toggle) {
        this.toggle.setAttribute('aria-label', this.userPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
        this.toggle.setAttribute('aria-pressed', String(this.userPaused));
      }
    }

    hold(on) {
      this.toggleAttribute('data-hold', on);
    }

    bindSwipe() {
      let x0 = null;
      let y0 = null;
      this.stage.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') { x0 = e.clientX; y0 = e.clientY; } });
      this.stage.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0;
        const dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        const forward = (dx < 0) !== (document.dir === 'rtl');
        this.go(this.index + (forward ? 1 : -1), true);
      });
      this.stage.addEventListener('pointercancel', () => { x0 = null; });
    }

    // Small "View" disc that follows the pointer over side looks (fine pointers only).
    bindCursor() {
      const cursor = $('[data-cursor]', this);
      if (!cursor || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      this.stage.addEventListener('pointermove', (e) => {
        const r = this.stage.getBoundingClientRect();
        const over = e.target.closest('[data-look]');
        cursor.style.transform = `translate(${e.clientX - r.left}px, ${e.clientY - r.top}px)`;
        cursor.toggleAttribute('data-visible', !!over && !over.hasAttribute('data-active'));
      });
      this.stage.addEventListener('pointerleave', () => cursor.removeAttribute('data-visible'));
    }

    bindPanel() {
      this.panels.forEach((panel) => {
        panel.addEventListener('change', (e) => {
          if (!e.target.matches('select[data-variant]')) return;
          const price = e.target.selectedOptions[0]?.dataset.price;
          const priceEl = $('.price', e.target.closest('[data-item]'));
          if (price && priceEl) priceEl.textContent = formatMoney(Number(price));
          this.updateTotal(panel);
        });
        panel.addEventListener('click', (e) => {
          const one = e.target.closest('[data-add-one]');
          if (one) this.add(panel, [one.closest('[data-item]')], one);
          const all = e.target.closest('[data-add-all]');
          if (all) this.add(panel, $$('[data-item][data-available]', panel), all);
        });
      });
    }

    selected(item) {
      const field = $('[data-variant]', item);
      if (!field) return null;
      const option = field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
      return option ? { id: field.value, price: Number(option.dataset.price || 0) } : null;
    }

    updateTotal(panel) {
      const total = $$('[data-item][data-available]', panel).reduce((sum, item) => sum + (this.selected(item)?.price || 0), 0);
      const el = $('[data-total]', panel);
      if (el) el.textContent = formatMoney(total);
    }

    async add(panel, items, btn) {
      if (btn.getAttribute('aria-busy') === 'true') return;
      const picks = items.map((item) => this.selected(item)).filter(Boolean);
      if (!picks.length) return;
      const error = $('[data-error]', panel);
      if (error) error.hidden = true;
      const form = new FormData();
      picks.forEach((p, i) => {
        form.append(`items[${i}][id]`, p.id);
        form.append(`items[${i}][quantity]`, '1');
      });
      btn.setAttribute('aria-busy', 'true');
      try {
        await SIM.cart.add(form);
        SIM.cart.open(btn);
      } catch (err) {
        if (error) { error.textContent = err.message; error.hidden = false; }
        else if (SIM.toast) SIM.toast(err.message);
      } finally {
        btn.removeAttribute('aria-busy');
      }
    }
  });
}
